<!-- CouponForm.vue -->
<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { storeToRefs } from "pinia";

import { toTitleCase } from "@/utils/stringUtils";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";

import { useCouponStore } from "@/stores/useCouponStore";

import AddCouponForm from "../Coupon/AddCouponForm.vue";
import EditCouponForm from "../Coupon/EditCouponForm.vue";
import CouponCardModal from "../Coupon/CouponCardModal.vue";
import DeleteCouponForm from "../Coupon/DeleteCouponForm.vue";
import BatchDeleteCouponForm from "../Coupon/BatchDeleteCouponForm.vue";
import CouponHelpModal from "./CouponHelpModal.vue";

import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

const showHelpModal = ref(false);

// Stores
const couponStore = useCouponStore();

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);
const isLoading = ref(true);

// IDs dos coupons cujo status está sendo atualizado.
const updatingStatusIds = ref([]);

// Dropdowns
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// Modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);
const showCardModal = ref(false);

// Seleção ativa
const selectedCouponId = ref(null);

// ── Toast ────────────────────────────────────────────────────────────────────
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}
function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// ── Fetch ────────────────────────────────────────────────────────────────────
async function fetchCouponsPaged() {
  isLoading.value = true;
  try {
    await couponStore.fetchCouponsPaged(currentPage.value, pageSize.value);
  } catch (error) {
    console.error("❌ Erro ao buscar coupons paginados:", error.message);
    showErrorToast("Falha ao carregar a lista de coupons.");
  } finally {
    isLoading.value = false;
  }
}

watch(currentPage, fetchCouponsPaged);

// ── Sincroniza currentPage local com store ───────────────────────────────────
watch(
  () => couponStore.currentPage,
  (newPage) => {
    if (couponStore.shouldForcePageOne && newPage === 1) {
      currentPage.value = 1;
      couponStore.shouldForcePageOne = false;
    }
  }
);

// ── Filtros de dropdown ──────────────────────────────────────────────────────
const statusFilter = computed({
  get: () => couponStore.filters.status,
  set: (value) => couponStore.setFilter("status", value),
});

const eligibleRuleFilter = computed({
  get: () => couponStore.filters.eligibleRule,
  set: (value) => couponStore.setFilter("eligibleRule", value),
});

const applyToAllCyclesFilter = computed({
  get: () => couponStore.filters.applyToAllCycles,
  set: (value) => couponStore.setFilter("applyToAllCycles", value),
});

const STATUS_OPTIONS = [
  { value: "", label: "Todos os status" },
  { value: true, label: "Ativo" },
  { value: false, label: "Inativo" },
];

const ELIGIBLE_RULE_OPTIONS = [
  { value: "", label: "Todas as regras" },
  { value: "ANYONE", label: "Anyone" },
  { value: "TARGETED", label: "Targeted" },
  { value: "FIRST_PAID", label: "First Paid" },
];

const APPLY_CYCLES_OPTIONS = [
  { value: "", label: "Todos os ciclos" },
  { value: true, label: "Todos os ciclos (recorrentes)" },
  { value: false, label: "Apenas primeiro ciclo" },
];

// ── Store refs ───────────────────────────────────────────────────────────────
const { filteredCoupons } = storeToRefs(couponStore);

// ── Seleção ──────────────────────────────────────────────────────────────────
const isAllSelectedOnPage = computed({
  get: () => {
    const idsOnPage = filteredCoupons.value.map((c) => c.couponId);
    if (idsOnPage.length === 0) return false;
    return idsOnPage.every((id) => couponStore.selectedCouponIds.includes(id));
  },
  set: (value) => {
    const idsOnPage = filteredCoupons.value.map((c) => c.couponId);
    if (value) {
      idsOnPage.forEach((id) => {
        if (!couponStore.selectedCouponIds.includes(id)) {
          couponStore.selectedCouponIds.push(id);
        }
      });
    } else {
      couponStore.selectedCouponIds = couponStore.selectedCouponIds.filter(
        (id) => !idsOnPage.includes(id)
      );
    }
  },
});

const canBatchActions = computed(
  () =>
    Array.isArray(couponStore.selectedCouponIds) &&
    couponStore.selectedCouponIds.length > 0
);

function toggleSelection(id) {
  couponStore.toggleSelection(id);
}

// ── Row click ────────────────────────────────────────────────────────────────
function indexSelector(index) {
  const coupon = filteredCoupons.value[index];
  if (!coupon) return;
  selectedCouponId.value = coupon.couponId;
  couponStore.selectedCoupon = coupon;
}

function handleRowClick(index) {
  indexSelector(index);
  showCardModal.value = true;
}

function handleChildNotify(payload) {
  if (!payload) return;
  if (payload.type === "error")
    showErrorToast(payload.message || "Ocorreu um erro.");
  else showSuccessToast(payload.message || "Operação realizada com sucesso.");
}

// ── Dropdowns ────────────────────────────────────────────────────────────────
function toggleRowDropdown(couponId) {
  openDropdownId.value = openDropdownId.value === couponId ? null : couponId;
}

function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

// ── Ações ────────────────────────────────────────────────────────────────────
function handleEdit(index) {
  indexSelector(index);
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  indexSelector(index);
  showCardModal.value = true;
  openDropdownId.value = null;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function tryOpenBatchDeleteModal() {
  const selectedIds = couponStore.selectedCouponIds;
  if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
    showErrorToast("Nenhum coupon selecionado.");
    isBatchDropdownOpen.value = false;
    return;
  }
  showBatchDeleteModal.value = true;
  isBatchDropdownOpen.value = false;
}

// ── Handler único pós-mudança ────────────────────────────────────────────────
async function handleCouponsChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeAdd = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    clearSelection = false,
  } = options;

  if (closeAdd) showAddModal.value = false;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;

  if (clearSelection) couponStore.selectedCouponIds = [];

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchCouponsPaged();
    return;
  }
  await fetchCouponsPaged();
}

// ── Status toggle ────────────────────────────────────────────────────────────
async function handleStatusToggle(coupon) {
  if (!coupon?.couponId) return;

  const couponId = coupon.couponId;
  const nextStatus = !coupon.isActive;

  if (updatingStatusIds.value.includes(couponId)) return;

  updatingStatusIds.value.push(couponId);

  try {
    await couponStore.updateCouponStatus({
      couponId,
      isActive: nextStatus,
    });

    showSuccessToast(
      `Status do coupon "${coupon.code}" alterado para ${
        nextStatus ? "ativo" : "inativo"
      } com sucesso!`
    );
  } catch (error) {
    console.error("❌ Falha ao atualizar o status do coupon:", error);
    showErrorToast(
      error?.response?.data?.message ||
        error?.message ||
        "Ocorreu um erro ao atualizar o status."
    );
  } finally {
    updatingStatusIds.value = updatingStatusIds.value.filter(
      (id) => id !== couponId
    );
  }
}

// ── Formatação de data ───────────────────────────────────────────────────────
function formatDateOnly(dateValue) {
  if (!dateValue) return "";
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// ── Verifica se o coupon está expirado ───────────────────────────────────────
function isExpired(coupon) {
  if (!coupon?.expiresAt) return false;
  const d = new Date(coupon.expiresAt);
  if (isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

function getRuleClass(rule) {
  const r = (rule ?? "").toLowerCase();
  if (r === "anyone") return "status-tag status-green";
  if (r === "targeted") return "status-tag status-blue";
  if (r === "first_paid") return "status-tag status-orange";
  if (r === "public") return "status-tag status-yellow";
  return "status-tag status-gray";
}

function getCyclesClass(applyToAllCycles) {
  return applyToAllCycles
    ? "status-tag status-blue"
    : "status-tag status-yellow";
}

function getCyclesLabel(applyToAllCycles) {
  return applyToAllCycles ? "Todos os ciclos" : "Apenas 1º ciclo";
}

// ── Resgates ─────────────────────────────────────────────────────────────────
function getRedemptionsDisplay(coupon) {
  const used = Number(coupon?.redemptionsCount ?? 0);
  const max = coupon?.maxRedemptions;
  if (max === null || max === undefined || max === "") {
    return `${used} / ∞`;
  }
  return `${used} / ${max}`;
}

function getRedemptionsClass(coupon) {
  const used = Number(coupon?.redemptionsCount ?? 0);
  return used > 0 ? "chips-note-count active-notes" : "chips-note-count";
}

// ── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(async () => {
  document.addEventListener("click", handleClickOutside);
  await fetchCouponsPaged();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);

  couponStore.clearFilters(false);
  couponStore.selectedCouponIds = [];
  openDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  showAddModal.value = false;
  showEditModal.value = false;
  showDeleteModal.value = false;
  showBatchDeleteModal.value = false;
  showCardModal.value = false;
  isLoading.value = false;
});
</script>

<template>
  <div class="w-full h-full">
    <div class="coupon-container">
      <div class="coupon-header">
        <div class="flex flex-col gap-0">
          <div class="flex items-center text-xl font-bold space-x-2">
            <span>Coupons</span>
          </div>
          <div class="text-sm mt-1 text-gray-700 ">
            Gerencie coupons de desconto, regras de elegibilidade e ciclos de
            aplicação.
            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <div class="filters-row">
          <div class="filters-group w-full">
            <input
              type="text"
              class="search-input-temp"
              placeholder="Buscar por código..."
              :value="couponStore.filters.search"
              @input="couponStore.setFilter('search', $event.target.value)"
            />

            <select v-model="statusFilter" class="filter-select">
              <option
                v-for="opt in STATUS_OPTIONS"
                :key="`status-${opt.value}`"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>

            <select v-model="eligibleRuleFilter" class="filter-select">
              <option
                v-for="opt in ELIGIBLE_RULE_OPTIONS"
                :key="`rule-${opt.value}`"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>

            <select v-model="applyToAllCyclesFilter" class="filter-select">
              <option
                v-for="opt in APPLY_CYCLES_OPTIONS"
                :key="`cycles-${opt.value}`"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>

        <div class="actions-row">
          <button @click="showAddModal = true" class="btn btn-primary">
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Adicionar Coupon
          </button>

          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-batch w-full"
              :disabled="!canBatchActions"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                :class="{ open: isBatchDropdownOpen }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>

            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <ul class="p-0 m-0 list-none"></ul>
              <div class="py-1">
                <a
                  href="#"
                  role="button"
                  @click.prevent="tryOpenBatchDeleteModal"
                  class="dropdown-item danger-item"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="coupon-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                />
              </th>
              <th style="width: 10%">Ações</th>
              <th style="width: 5%">Id</th>
              <th style="width: 16%">Código</th>
              <th style="width: 8%">Desconto</th>
              <th style="width: 10%">Regra</th>
              <th
                style="width: 9%"
                title="Resgates efetivos / teto (max_redemptions)"
              >
                Resgates
              </th>
              <th style="width: 11%">Expira em</th>
              <th style="width: 12%">Ciclos</th>
              <th style="width: 8%">Ativo</th>
              <th style="width: 11%">Criado em</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(coupon, index) in !isLoading ? filteredCoupons : []"
              :key="coupon.couponId"
              @click="handleRowClick(index)"
              class="clickable-row"
            >
              <td @click.stop>
                <input
                  type="checkbox"
                  :value="coupon.couponId"
                  :checked="
                    couponStore.selectedCouponIds?.includes(coupon.couponId)
                  "
                  @change="toggleSelection(coupon.couponId)"
                  class="checkbox-style"
                />
              </td>

              <td class="action-cell-container" @click.stop>
                <div class="row-actions">
                  <button
                    @click="toggleRowDropdown(coupon.couponId)"
                    class="btn-icon btn-soft-brand"
                    title="Menu de Ações"
                  >
                    <i class="fas fa-ellipsis-h fa-lg"></i>
                  </button>
                </div>

                <div
                  v-if="openDropdownId === coupon.couponId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li @click="handleEdit(index)">
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li @click="handleView(index)">
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>

                    <li class="danger-item" @click="handleDelete(index)">
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="coupon.couponId" style="font-size: smaller">{{
                  coupon.couponId
                }}</span>
              </td>

              <td class="cell-text-center">
                <span :title="coupon.code" class="coupon-code-cell">
                  {{ coupon.code }}
                </span>
              </td>

              <td>
                <span class="status-tag discount-badge">
                  {{ coupon.discountPercent }}%
                </span>
              </td>

              <td>
                <span :class="getRuleClass(coupon.eligibleRule)">
                  {{ toTitleCase(coupon.eligibleRule ?? "") }}
                </span>
              </td>

              <td>
                <span
                  :class="getRedemptionsClass(coupon)"
                  :title="`Resgates efetivos / teto`"
                >
                  {{ getRedemptionsDisplay(coupon) }}
                </span>
              </td>

              <td>
                <span
                  :title="formatDateTimeForDisplay(coupon.expiresAt)"
                  :class="[
 'expires-cell',
                    { 'expires-expired': isExpired(coupon) },
                  ]"
                  style="font-size: smaller"
                >
                  {{ formatDateOnly(coupon.expiresAt) }}
                </span>
              </td>

              <td>
                <span :class="getCyclesClass(coupon.applyToAllCycles)">
                  {{ getCyclesLabel(coupon.applyToAllCycles) }}
                </span>
              </td>

              <td @click.stop>
                <label class="toggle-switch table-status-toggle">
                  <input
                    type="checkbox"
                    :checked="coupon.isActive"
                    :disabled="updatingStatusIds.includes(coupon.couponId)"
                    @change="handleStatusToggle(coupon)"
                  />
                  <span class="slider"></span>
                </label>
              </td>

              <td>
                <span
                  :title="formatDateTimeForDisplay(coupon.createdAt)"
                  style="font-size: smaller"
                >
                  {{ formatDateOnly(coupon.createdAt) }}
                </span>
              </td>
            </tr>

            <tr v-if="isLoading" class="empty-row-state">
              <td colspan="11" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando coupons...</p>
                </div>
              </td>
            </tr>

            <tr
              v-else-if="couponStore.totalCount === 0"
              class="empty-row-state"
            >
              <td colspan="11" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhum coupon encontrado.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoading && couponStore.totalCount > 0"
          class="coupon-pagination-row"
        >
          <div class="coupon-pagination-left">
            <span class="coupon-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="couponStore.totalCount"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="couponStore.totalCount"
            :total-pages="couponStore.totalPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AddCouponForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @refresh="handleCouponsChanged({ goToFirstPage: true, closeAdd: true })"
    />

    <EditCouponForm
      v-if="showEditModal"
      :couponId="selectedCouponId"
      @close="showEditModal = false"
      @refresh="
        handleCouponsChanged({ goToFirstPage: false, closeEdit: true })
      "
    />

    <CouponCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :couponId="selectedCouponId"
      @close="showCardModal = false"
    />

    <CouponHelpModal :show="showHelpModal" @close="showHelpModal = false" />

    <DeleteCouponForm
      v-if="showDeleteModal"
      :coupon="couponStore.selectedCoupon"
      @close="showDeleteModal = false"
      @refresh="
        handleCouponsChanged({
          goToFirstPage: true,
          closeDelete: true,
          clearSelection: true,
        })
      "
      @notify="handleChildNotify"
    />

    <BatchDeleteCouponForm
      v-if="showBatchDeleteModal"
      @close="showBatchDeleteModal = false"
      @refresh="
        handleCouponsChanged({
          goToFirstPage: true,
          closeBatchDelete: true,
          clearSelection: true,
        })
      "
      @notify="handleChildNotify"
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.coupon-container {
  background-color: var(--bg-content);
  border-radius: 16px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-size: 0.95rem;
}

.coupon-header {
  background: var(--bg-header);
  padding: 0.75rem 1.5rem;
  color: var(--text-dark);
}

.filters-row {
  display: flex;
  width: 100%;
}

.actions-row {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}

@media (max-width: 767px) {
  .actions-row {
    flex-direction: column;
    align-items: stretch;
  }
}

.action-bar input,
.action-bar .search-input,
.search-text-input input {
  font-size: 0.8rem !important;
}

.filters-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

@media (min-width: 768px) {
  .filters-group {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }
}

.filter-select {
  height: 32px;
  padding: 0.3rem 1.6rem 0.3rem 0.5rem;
  font-size: 0.8rem;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  min-width: 160px;
  box-sizing: border-box;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.45rem center;
  background-size: 18px 18px;
}
.filter-select:hover {
  border-color: var(--focus-border);
}
.filter-select:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

.search-input-temp {
  width: 100%;
  height: 32px;
  padding: 0.3rem 0.5rem;
  font-size: 0.8rem;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  box-sizing: border-box;
  flex: 0 1 50%;
  min-width: 160px;
  max-width: 50%;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.search-input-temp:hover {
  border-color: var(--focus-border);
}
.search-input-temp:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

.dropdown-divider {
  height: 1px;
  background-color: var(--border-color);
  margin: 6px 0;
}
.dropdown-toggle-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.65rem;
  cursor: default;
}
.label-text {
  font-size: 0.8rem;
  color: var(--text-dark);
}
.row-dropdown-menu .toggle-switch {
  width: 38px;
  height: 20px;
}
.row-dropdown-menu .slider {
  border-radius: 999px;
}
.row-dropdown-menu .slider:before {
  width: 14px;
  height: 14px;
  left: 3px;
  bottom: 3px;
}
.row-dropdown-menu input:checked + .slider:before {
  transform: translateX(18px);
}

.coupon-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  text-align: center;
}
.coupon-table th {
  padding: 0.6rem 0.5rem;
  text-transform: uppercase;
  font-size: 0.7rem;
  color: var(--text-dark);
  background: var(--bg-header);
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 10;
}
.coupon-table tr {
  border-bottom: 1px solid var(--border-color);
}
.coupon-table tbody tr:not(.empty-row-state):hover {
  background-color: var(--bg-active-nav);
}
.coupon-table td {
  padding: 0.3rem 0.4rem;
  font-size: 0.8rem;
  vertical-align: middle;
}
.coupon-table .cell-text-left {
  text-align: left;
}
.coupon-table .cell-text-center {
  text-align: center;
}
.coupon-table td > span,
.coupon-table td > div {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0 auto;
}
.coupon-table td > div {
  display: inline-block;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.coupon-code-cell {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: center;
}

.expires-cell.expires-expired {
  color: #c92a2a;
  font-weight: 600;
}

.status-tag {
  display: inline-block;
  padding: 0.125rem 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
}
.status-red {
  background-color: #ffc9c9;
  color: #c92a2a;
}
.status-orange {
  background-color: #ffd8a8;
  color: #e8590c;
}
.status-blue {
  background-color: #a5d8ff;
  color: #1971c2;
}
.status-green {
  background-color: #b2f2bb;
  color: #2b8a3e;
}
.status-yellow {
  background-color: #fff3bf;
  color: #f59f00;
}
.status-gray {
  background-color: var(--neutral-200);
  color: #495057;
}

.discount-badge {
  background-color: #e0dbff;
  color: var(--text-on-brand-soft);
  font-weight: 600;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--neutral-200);
  transition: 0.4s;
  border-radius: 28px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: var(--surface);
  transition: 0.4s;
  border-radius: 50%;
}
input:checked + .slider {
  background-color: var(--success);
}
input:checked + .slider:before {
  transform: translateX(22px);
}
input:disabled + .slider {
  cursor: not-allowed;
  opacity: 0.5;
}

.status-toggle-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}
.status-column-toggle {
  width: 38px;
  height: 20px;
}
.status-column-toggle .slider:before {
  width: 14px;
  height: 14px;
  left: 3px;
  bottom: 3px;
}
.status-column-toggle input:checked + .slider:before {
  transform: translateX(18px);
}

.chips-note-count {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 28px;
  height: 22px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-dark);
  background-color: var(--neutral-200);
  border: 1px solid #ced4da;
  transition: all 0.2s;
}
.chips-note-count.active-notes {
  background-color: #ccf7ff;
  color: #1a4a4f;
  border-color: #9bf6ff;
}

.coupon-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.87rem;
}
.coupon-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.coupon-pagination-label {
  padding-top: 2px;
}

.table-status-toggle {
  width: 42px;
  height: 22px;
}
.table-status-toggle .slider:before {
  width: 16px;
  height: 16px;
  left: 3px;
  bottom: 3px;
}
.table-status-toggle input:checked + .slider:before {
  transform: translateX(20px);
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

.coupon-table td > div.empty-state-content {
  display: flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100% !important;
  max-width: none !important;
  min-height: 180px;
  margin: 0 auto !important;
  text-align: center;
}

</style>